import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PageSEO from "../components/PageSEO";
import {
  Sparkles, Zap, Shield, TrendingUp, ArrowRight,
  Clock, DollarSign, X, Gift, Facebook, Instagram, Youtube,
  Mail, CheckCircle, Target, Users, Star, Quote,
  Smartphone, CreditCard, Search, UserCheck, Bell, Lock, MapPin
} from "lucide-react";
import axios from "axios";
import JourneyShowcase from "../components/JourneyShowcase";
import SignUpBonusPromo from "../components/SignUpBonusPromo";
import CursorEffect from "../components/CursorEffect";
import { BACKEND_URL } from '../config/api';
import './Landing.css';

const API = `${BACKEND_URL}/api`;

const SERVICES = [
  { icon: "\u{1F9F9}", title: "Cleaning", desc: "Home, office, deep cleaning" },
  { icon: "\u{1F527}", title: "Repairs & Maintenance", desc: "Plumbing, electrical, HVAC" },
  { icon: "\u{1F3E0}", title: "Home Improvement", desc: "Painting, flooring, carpentry" },
  { icon: "\u{1F3A8}", title: "Interior Painting", desc: "Single rooms, touch-ups, accent walls" },
  { icon: "\u{1F4E6}", title: "Moving & Delivery", desc: "Moving, packing, junk removal" },
  { icon: "\u{1F5D1}\u{FE0F}", title: "Junk Removal", desc: "Haul away unwanted items fast" },
  { icon: "\u{2744}\u{FE0F}", title: "Snow Removal", desc: "Driveways, walkways, after-storm clearing" },
  { icon: "\u{1F333}", title: "Outdoor & Landscaping", desc: "Lawn care, snow, yard work" },
  { icon: "\u{1F415}", title: "Pet Care", desc: "Walking, sitting, grooming" },
  { icon: "\u{1F3E1}", title: "House Sitting", desc: "Plants, mail, short-stay sitting" },
  { icon: "\u{1F389}", title: "Events & Entertainment", desc: "Setup, catering, decorating" },
  { icon: "\u{1F4BB}", title: "Tech & Digital", desc: "Tech support, web design" },
  { icon: "\u{1F4F8}", title: "Photography & Media", desc: "Photos, video, translation" },
  { icon: "\u{1F4DA}", title: "Tutoring & Lessons", desc: "Academic, music, languages" },
  { icon: "\u{1F697}", title: "Auto & Vehicle", desc: "Detailing, car wash, bikes" },
  { icon: "\u{1F9FA}", title: "Organization & Decluttering", desc: "Closets, garages, Marie-Kondo-style" },
  { icon: "\u{1F6D2}", title: "Errands & Shopping", desc: "Grocery runs, pickups, post-office drops" },
  { icon: "\u{1F9FA}", title: "Laundry & Ironing", desc: "Pickup, wash, fold, return" },
  { icon: "\u{1F9F5}", title: "Clothing Alteration", desc: "Tailoring, hemming, mending" },
];

const TESTIMONIALS = [
  { name: "Sarah M.", role: "Client", city: "Toronto", text: "Found an amazing cleaner within minutes. The whole process was seamless — from booking to payment.", rating: 5 },
  { name: "Marcus J.", role: "Hero", city: "Vancouver", text: "I've tried every gig platform out there. taskeri® is the only one that doesn't charge me just to see a lead.", rating: 5 },
  { name: "Priya K.", role: "Client", city: "Calgary", text: "Real-time tracking is a game changer. I could see exactly when my handyman was arriving.", rating: 5 },
  { name: "David L.", role: "Hero", city: "Montreal", text: "Finally a platform that respects my time. Great clients, fair pay, and the app is super easy to use.", rating: 5 },
  { name: "Jennifer W.", role: "Client", city: "Ottawa", text: "Booked a snow removal hero during a blizzard. He arrived in 30 minutes and did an incredible job!", rating: 5 },
  { name: "Ryan T.", role: "Hero", city: "Edmonton", text: "Transfers land like clockwork once the hold clears, and Stripe pays my bank daily. No more chasing clients for money like other platforms.", rating: 5 },
  { name: "Michelle C.", role: "Client", city: "Winnipeg", text: "Used taskeri® for moving help. The heroes were professional, fast, and didn't damage anything.", rating: 5 },
  { name: "Kevin H.", role: "Hero", city: "Halifax", text: "I set my own rates and schedule. Best side hustle I've ever had — highly recommend!", rating: 5 },
  { name: "Amanda R.", role: "Client", city: "Victoria", text: "Pet sitting through taskeri® was worry-free. Got photo updates and my dog loved his walker!", rating: 5 },
  { name: "Chris B.", role: "Hero", city: "Quebec City", text: "The verification process shows clients I'm legit. I get more bookings because of the trust badge.", rating: 5 },
  { name: "Laura P.", role: "Client", city: "Mississauga", text: "Needed last-minute event help for my daughter's party. taskeri® saved the day — absolutely fantastic!", rating: 5 },
  { name: "James N.", role: "Hero", city: "Saskatoon", text: "Love that I can pick jobs near me. Less driving, more earning. The AI matching really works.", rating: 5 },
  { name: "Sophie G.", role: "Client", city: "Hamilton", text: "The escrow system gives me peace of mind. I only pay when I'm 100% satisfied with the work.", rating: 5 },
  { name: "Tyler M.", role: "Hero", city: "London", text: "Switched from other gig apps and doubled my income. The client quality here is unmatched.", rating: 5 },
  { name: "Nina S.", role: "Client", city: "Brampton", text: "Hired a tech support hero to set up my home office. Professional, patient, and reasonably priced!", rating: 5 },
  { name: "Derek W.", role: "Hero", city: "Surrey", text: "The app notifications are instant. I never miss a job opportunity in my area anymore.", rating: 5 },
];

const HOW_IT_WORKS = [
  { step: "01", icon: Search, title: "Post Your Job", desc: "Describe what you need, set your budget, and choose instant or scheduled." },
  { step: "02", icon: UserCheck, title: "Get Matched", desc: "AI helps you match with verified Heroes. Compare bids and pick the best fit." },
  { step: "03", icon: Bell, title: "Track in Real-Time", desc: "Watch your Hero arrive and get live updates throughout the job if you want." },
  { step: "04", icon: CreditCard, title: "Pay Securely", desc: "Funds are held in escrow and released only when you're satisfied." },
];

// Scroll-triggered animated review card
const ReviewCard = ({ testimonial: t, index: i }) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Add staggered delay based on index
          setTimeout(() => setIsVisible(true), (i % 4) * 100);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "50px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [i]);

  return (
    <div 
      ref={cardRef}
      className={`lp-review-card lp-review-${i % 4} ${isVisible ? 'lp-review-visible' : ''}`} 
      data-testid={`testimonial-${i}`}
    >
      <div className="lp-review-quote">"</div>
      <p className="lp-review-text">{t.text}</p>
      <div className="lp-review-bottom">
        <div className="lp-review-stars">
          {Array.from({ length: t.rating }).map((_, j) => (
            <Star key={j} size={12} fill="#b45309" color="#b45309" />
          ))}
        </div>
        <div className="lp-review-author">
          <div className="lp-review-avatar">{t.name[0]}</div>
          <div>
            <strong>{t.name}</strong>
            <span>{t.role} · {t.city}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const Landing = () => {
  const navigate = useNavigate();
  const [showBanner, setShowBanner] = useState(false);
  const [newsletterStatus, setNewsletterStatus] = useState(null);
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const marqueeRef = useRef(null);
  const ctaRef = useRef(null);
  const [devEmail, setDevEmail] = useState('');
  const [devLoading, setDevLoading] = useState(false);
  const [devError, setDevError] = useState('');
  const isPreview = typeof window !== 'undefined' && window.location.hostname.includes('preview.emergentagent.com');

  useEffect(() => {
    // Stagger: hold the referral banner until cookie consent is handled
    let t1, t2;
    const startBanner = (delay) => {
      t1 = setTimeout(() => setShowBanner(true), delay);
      t2 = setTimeout(() => setShowBanner(false), delay + 10000);
    };
    const onConsent = () => startBanner(1000);
    if (localStorage.getItem('taskeri_cookie_consent')) {
      startBanner(2000);
    } else {
      window.addEventListener('taskeri:cookie-consent', onConsent, { once: true });
    }
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('taskeri:cookie-consent', onConsent);
    };
  }, []);

  // Marquee tile swap effect — visible arc path
  useEffect(() => {
    let swapping = false;
    const lastSwapped = { top: -1, bottom: -1 };
    const interval = setInterval(() => {
      if (swapping || !marqueeRef.current) return;
      const section = marqueeRef.current;
      const sectionRect = section.getBoundingClientRect();
      if (sectionRect.bottom < 0 || sectionRect.top > window.innerHeight) return;

      const topTrack = section.querySelector('[data-row="top"] .lp-marquee-track');
      const bottomTrack = section.querySelector('[data-row="bottom"] .lp-marquee-track');
      const topCards = [...section.querySelectorAll('[data-row="top"] .lp-service-card')];
      const bottomCards = [...section.querySelectorAll('[data-row="bottom"] .lp-service-card')];
      if (!topCards.length || !bottomCards.length || !topTrack || !bottomTrack) return;

      // Get all visible cards with their indices
      const getVisible = (cards) => {
        return cards.reduce((acc, c, i) => {
          const r = c.getBoundingClientRect();
          if (r.right > 0 && r.left < window.innerWidth) acc.push({ el: c, idx: i });
          return acc;
        }, []);
      };

      const visibleTop = getVisible(topCards).filter(c => c.idx !== lastSwapped.top);
      const visibleBottom = getVisible(bottomCards).filter(c => c.idx !== lastSwapped.bottom);
      if (!visibleTop.length || !visibleBottom.length) return;

      const pickA = visibleTop[Math.floor(Math.random() * visibleTop.length)];
      const pickB = visibleBottom[Math.floor(Math.random() * visibleBottom.length)];
      lastSwapped.top = pickA.idx;
      lastSwapped.bottom = pickB.idx;

      const cardA = pickA.el;
      const cardB = pickB.el;
      swapping = true;

      // Smoothly pause marquee tracks
      [topTrack, bottomTrack].forEach(t => {
        const cs = getComputedStyle(t);
        t.style.transform = cs.transform;
        t.style.animationPlayState = 'paused';
      });

      const rectA = cardA.getBoundingClientRect();
      const rectB = cardB.getBoundingClientRect();
      const midY = (rectA.top + rectB.top) / 2;

      const makeClone = (card, from) => {
        const clone = card.cloneNode(true);
        clone.className = 'lp-service-card';
        Object.assign(clone.style, {
          position: 'fixed', left: from.left + 'px', top: from.top + 'px',
          width: from.width + 'px', height: from.height + 'px',
          zIndex: 10000, margin: 0, pointerEvents: 'none',
          transition: 'none', opacity: 1,
          boxShadow: '0 8px 32px rgba(132, 72, 252,0.3)',
          borderColor: 'rgba(129,140,248,0.7)',
          transform: 'scale(1)',
        });
        document.body.appendChild(clone);
        return clone;
      };

      cardA.style.transition = 'opacity 0.15s';
      cardB.style.transition = 'opacity 0.15s';
      cardA.style.opacity = '0';
      cardB.style.opacity = '0';
      const cloneA = makeClone(cardA, rectA);
      const cloneB = makeClone(cardB, rectB);

      // Phase 1: lift to midpoint
      requestAnimationFrame(() => {
        [cloneA, cloneB].forEach(c => {
          c.style.transition = 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)';
        });
        requestAnimationFrame(() => {
          cloneA.style.top = (midY - rectA.height / 2) + 'px';
          cloneA.style.transform = 'scale(1.08)';
          cloneA.style.boxShadow = '0 16px 48px rgba(132, 72, 252,0.35)';
          cloneB.style.top = (midY - rectB.height / 2) + 'px';
          cloneB.style.transform = 'scale(1.08)';
          cloneB.style.boxShadow = '0 16px 48px rgba(132, 72, 252,0.35)';
        });
      });

      // Phase 2: cross to target
      setTimeout(() => {
        [cloneA, cloneB].forEach(c => {
          c.style.transition = 'all 0.45s cubic-bezier(0.34, 1.2, 0.64, 1)';
        });
        requestAnimationFrame(() => {
          cloneA.style.left = rectB.left + 'px';
          cloneA.style.top = rectB.top + 'px';
          cloneA.style.transform = 'scale(1)';
          cloneA.style.boxShadow = '0 4px 16px rgba(132, 72, 252,0.12)';
          cloneB.style.left = rectA.left + 'px';
          cloneB.style.top = rectA.top + 'px';
          cloneB.style.transform = 'scale(1)';
          cloneB.style.boxShadow = '0 4px 16px rgba(132, 72, 252,0.12)';
        });
      }, 430);

      // Phase 3: settle — crossfade clones out, originals in, resume marquee
      setTimeout(() => {
        // Swap DOM content
        const tmpHtml = cardA.innerHTML;
        cardA.innerHTML = cardB.innerHTML;
        cardB.innerHTML = tmpHtml;
        const tmpS = cardA.dataset.service;
        cardA.dataset.service = cardB.dataset.service;
        cardB.dataset.service = tmpS;

        // Crossfade
        [cloneA, cloneB].forEach(c => {
          c.style.transition = 'opacity 0.3s ease';
          c.style.opacity = '0';
        });
        cardA.style.transition = 'opacity 0.3s ease';
        cardB.style.transition = 'opacity 0.3s ease';
        requestAnimationFrame(() => {
          cardA.style.opacity = '1';
          cardB.style.opacity = '1';
        });

        // Resume marquee smoothly
        setTimeout(() => {
          [topTrack, bottomTrack].forEach(t => {
            t.style.transform = '';
            t.style.animationPlayState = '';
          });
          cloneA.remove();
          cloneB.remove();
          [cardA, cardB].forEach(c => { c.style.transition = ''; });
          swapping = false;
        }, 350);
      }, 920);
    }, 6000);

    return () => clearInterval(interval);
  }, []);

  // CTA section grow & shrink on scroll
  useEffect(() => {
    const el = ctaRef.current;
    if (!el) return;
    const onScroll = () => {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      // Progress: 0 when entering bottom, 1 when centered, 0 when exiting top
      const center = rect.top + rect.height / 2;
      const progress = 1 - Math.abs(center - vh / 2) / (vh / 2);
      const clamped = Math.max(0, Math.min(1, progress));
      const scale = 0.88 + clamped * 0.12; // 0.88 → 1.0
      const opacity = 0.6 + clamped * 0.4; // 0.6 → 1.0
      el.style.transform = `scale(${scale})`;
      el.style.opacity = opacity;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const goAuth = () => {
    const url = window.location.origin;
    window.location.href = `${url}/api/auth/google/login`;
  };

  const handleDevLogin = async (e) => {
    e.preventDefault();
    if (!devEmail.trim() || devLoading) return;
    setDevLoading(true);
    setDevError('');
    try {
      await axios.post(`${API}/auth/dev-login`, { email: devEmail.trim().toLowerCase() }, { withCredentials: true });
      localStorage.setItem('has_session', 'true');
      window.location.href = '/dashboard?auth=success';
    } catch (err) {
      setDevError(err.response?.data?.detail || 'Login failed');
    } finally {
      setDevLoading(false);
    }
  };

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (newsletterLoading) return;
    const input = e.target.querySelector("input");
    const email = input.value.trim();
    if (!email) return;
    setNewsletterLoading(true);
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, { email });
      setNewsletterStatus(res.data.status === "already_subscribed" ? "already" : "success");
      input.value = "";
    } catch { setNewsletterStatus("error"); }
    setNewsletterLoading(false);
    setTimeout(() => setNewsletterStatus(null), 4000);
  };

  return (
    <>
      <PageSEO
        title={null}
        description="taskeri® connects you with verified Task Heroes for home services, cleaning, moving, deliveries & more across Canada. Book trusted local help in minutes."
        path="/"
      />
      <div className="lp" data-testid="landing-page">
        {/* Referral Banner */}
        <div className={`lp-banner ${showBanner ? 'lp-banner-visible' : ''}`}>
          <div className="lp-banner-inner">
            <Gift size={18} className="lp-banner-icon" />
            <span className="lp-banner-text" onClick={goAuth}><strong>Get $10 When Your Friend Joins!</strong><span className="lp-banner-detail"> Refer friends and both earn $10 credit</span></span>
            <button className="lp-banner-cta" onClick={goAuth}>Learn More</button>
            <button className="lp-banner-close" onClick={() => setShowBanner(false)} aria-label="Close"><X size={16} /></button>
          </div>
        </div>

        {/* ───── HERO ───── */}
        <section className="lp-hero">
          <CursorEffect type="particles" />
          <div className="lp-hero-content">
            <div className="lp-hero-left">
              <div className="lp-badge" data-testid="hero-badge"><Sparkles size={16} /><span>Powered by AI</span></div>
              <h1 className="lp-h1" data-testid="hero-title">
                Hire trusted local Heroes.<br /><span className="lp-h1-accent">Pay only when the job is done.</span>
              </h1>
              <p className="lp-hero-desc" data-testid="hero-description">
                A new On-Demand Services Marketplace where clients and heroes both win. No fake leads. Connect with verified Heroes using AI-powered matching.
              </p>
              <div className="lp-hero-avail" data-testid="hero-availability-badge"><MapPin size={14} /><span>Available across Canada</span></div>
              <div className="lp-hero-btns">
                <button className="lp-btn lp-btn-primary neon-glow-indigo" onClick={goAuth} data-testid="get-started-btn">Get Started <ArrowRight size={18} /></button>
                <button className="lp-btn lp-btn-outline" onClick={goAuth} data-testid="become-hero-btn">Become a Hero</button>
              </div>
              <p className="lp-hero-sub">Already have an account? <button className="lp-link" onClick={goAuth} data-testid="returning-login-link">Sign in</button></p>
              {isPreview && (
                <div data-testid="dev-login-panel" style={{
                  marginTop: 16, padding: '16px 20px', background: 'rgba(251,191,36,0.1)',
                  border: '1.5px dashed #b45309', borderRadius: 12, maxWidth: 380
                }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: '#b45309', margin: '0 0 8px' }}>
                    Preview Mode — Dev Login
                  </p>
                  <form onSubmit={handleDevLogin} style={{ display: 'flex', gap: 8 }}>
                    <input
                      type="email"
                      value={devEmail}
                      onChange={(e) => setDevEmail(e.target.value)}
                      placeholder="Enter email to login"
                      data-testid="dev-login-email"
                      style={{
                        flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid #334155',
                        background: '#0f172a', color: '#e2e8f0', fontSize: 14, outline: 'none'
                      }}
                    />
                    <button
                      type="submit"
                      disabled={devLoading}
                      data-testid="dev-login-submit"
                      style={{
                        padding: '8px 16px', borderRadius: 8, border: 'none',
                        background: '#b45309', color: '#ffffff', fontWeight: 600,
                        fontSize: 13, cursor: devLoading ? 'wait' : 'pointer', whiteSpace: 'nowrap'
                      }}
                    >
                      {devLoading ? '...' : 'Login'}
                    </button>
                  </form>
                  {devError && <p style={{ color: '#dc2626', fontSize: 12, margin: '6px 0 0' }}>{devError}</p>}
                </div>
              )}
            </div>
            <div className="lp-hero-right">
              <div className="lp-float-card lp-fc1"><Zap size={28} /><div className="lp-fc-title">Instant Bidding</div><p>Choose the right Heroes.</p></div>
              <div className="lp-float-card lp-fc2"><Shield size={28} /><div className="lp-fc-title">Verified Heroes</div><p>Identity verified professionals.</p></div>
              <div className="lp-float-card lp-fc3"><TrendingUp size={28} /><div className="lp-fc-title">Real-Time Tracking</div><p>Track your Hero in real time.</p></div>
            </div>
          </div>
        </section>

        {/* ───── STATS ───── */}
        <section className="lp-stats" data-testid="landing-stats">
          <div className="lp-stats-inner">
            {[
              { val: "1,000+", lbl: "Verified Heroes", color: "#8448fc" },
              { val: "3,000+", lbl: "Jobs Completed", color: "#0ea5e9" },
              { val: "4.9", lbl: "Average Rating", color: "#b45309" },
              { val: "98%", lbl: "Client Satisfaction", color: "#047857" },
            ].map((s) => (
              <div className="lp-stat" key={s.lbl}>
                <span className="lp-stat-val" style={{ color: s.color }}>{s.val}</span>
                <span className="lp-stat-lbl">{s.lbl}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ───── SIGN-UP BONUS PROMO ───── */}
        <SignUpBonusPromo onCta={goAuth} />

        {/* ───── HOW IT WORKS ───── */}
        <section className="lp-how" data-testid="landing-how-it-works">
          <div className="lp-how-inner">
            <div className="lp-section-head">
              <div className="lp-badge-sm"><Clock size={14} /><span>Simple & Fast</span></div>
              <h2 className="lp-section-title">How taskeri<sup style={{fontSize:'0.55em'}}>®</sup> Works</h2>
              <p className="lp-section-sub">Get help in four easy steps</p>
            </div>
            <div className="lp-how-grid">
              {HOW_IT_WORKS.map((h) => {
                const Icon = h.icon;
                return (
                  <div className="lp-how-card" key={h.step} data-testid={`how-step-${h.step}`}>
                    <span className="lp-how-step">{h.step}</span>
                    <div className="lp-how-icon"><Icon size={22} /></div>
                    <h3 className="lp-how-title">{h.title}</h3>
                    <p className="lp-how-desc">{h.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ───── VIDEO ───── (hidden) */}
        {/* Video section hidden — headings moved to JourneyShowcase */}

        {/* ───── JOURNEY SHOWCASE ───── */}
        <JourneyShowcase />

        {/* ───── VALUE PROPOSITION ───── */}
        <section className="lp-value" data-testid="landing-value-prop">
          <CursorEffect type="spotlight" />
          <div className="lp-value-inner">
            <div className="lp-value-head">
              <div className="lp-value-badge"><DollarSign size={14} /><span>Why taskeri<sup style={{fontSize:'0.55em'}}>®</sup></span></div>
              <h2 className="lp-value-title">You're Not Paying for Ads</h2>
              <p className="lp-value-sub">You're paying for <span className="lp-value-accent">results</span>.</p>
            </div>
            <div className="lp-value-grid">
              {[
                { icon: Target, color: "#8448fc", title: "No Fake Leads", desc: "Real jobs from real clients. Every request is verified before it reaches Heroes." },
                { icon: Users, color: "#0ea5e9", title: "Fair Bidding", desc: "Set your own rates, compete on merit, and win jobs with your reputation." },
                { icon: CreditCard, color: "#047857", title: "Transparent Pricing", desc: "See the full cost breakdown before you book — platform fees, taxes, and hero earnings. No hidden charges, ever." },
              ].map((v, i) => {
                const Icon = v.icon;
                return (
                  <div className="lp-value-card" key={v.title} data-testid={`value-card-${i}`}>
                    <div className="lp-value-card-glow" style={{ background: `radial-gradient(circle at 50% 0%, ${v.color}20 0%, transparent 70%)` }}></div>
                    <div className="lp-value-icon" style={{ color: v.color }}><Icon size={28} /></div>
                    <h3 className="lp-value-card-title">{v.title}</h3>
                    <p className="lp-value-card-desc">{v.desc}</p>
                  </div>
                );
              })}
            </div>

            {/* Comparison Stats */}
            <div className="lp-value-compare">
              <div className="lp-compare-item">
                <div className="lp-compare-old">
                  <span className="lp-compare-label">Other Platforms</span>
                  <span className="lp-compare-val lp-compare-bad">$50-200</span>
                  <span className="lp-compare-note">per lead, no guarantee</span>
                </div>
                <div className="lp-compare-vs">VS</div>
                <div className="lp-compare-new">
                  <span className="lp-compare-label">taskeri<sup style={{fontSize:'0.55em'}}>®</sup></span>
                  <span className="lp-compare-val lp-compare-good">$0</span>
                  <span className="lp-compare-note">to see & bid on jobs</span>
                </div>
              </div>
            </div>

            {/* Bottom CTA */}
            <div className="lp-value-cta">
              <p className="lp-value-cta-text">Join thousands of Heroes earning on their own terms</p>
              <button className="lp-btn neon-glow-indigo" onClick={goAuth} data-testid="value-cta-btn">
                Start Earning Today <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </section>

        {/* ───── TRUST & SAFETY ───── */}
        <section className="lp-trust" data-testid="landing-trust">
          <div className="lp-trust-inner">
            <div className="lp-trust-text">
              <div className="lp-badge-sm-dark"><Lock size={14} /><span>Trust & Safety</span></div>
              <h2 className="lp-trust-title">Your safety is our priority</h2>
              <p className="lp-trust-desc">Every transaction on taskeri<sup style={{fontSize:'0.55em'}}>®</sup> is protected by multiple layers of security and verification.</p>
              <div className="lp-trust-features">
                {[
                  "Verified Clients & Heroes",
                  "Secure escrow payments",
                  "256-bit SSL encryption on all data",
                  "Full audit logging on sensitive actions",
                  "Right to export & delete your data",
                  "Granular cookie & consent controls",
                ].map((f) => (
                  <div className="lp-trust-feature" key={f}><CheckCircle size={16} /><span>{f}</span></div>
                ))}
              </div>
              <button className="lp-btn lp-btn-primary" onClick={() => navigate("/safety")} data-testid="trust-learn-more-btn">Learn More <ArrowRight size={16} /></button>
            </div>
            <div className="lp-trust-visual">
              <p className="lp-trust-comply-label">Compliant With</p>
              <div className="lp-trust-comply-grid" data-testid="compliance-section">
                {[
                  { name: "GDPR", full: "General Data Protection Regulation" },
                  { name: "PIPEDA", full: "Personal Information Protection" },
                  { name: "WCAG 2.1", full: "Web Accessibility Guidelines" },
                  { name: "USDP", full: "US Data Privacy Framework" },
                ].map((f) => (
                  <div className="lp-trust-comply-card" key={f.name} data-testid={`compliance-badge-${f.name.toLowerCase().replace(/\s+/g, '-')}`}>
                    <Shield size={18} />
                    <div>
                      <span className="lp-trust-comply-name">{f.name}</span>
                      <span className="lp-trust-comply-full">{f.full}</span>
                    </div>
                  </div>
                ))}
              </div>
              <a href="/accessibility" className="lp-trust-acc-link">Read our Accessibility Statement <ArrowRight size={14} /></a>
            </div>
          </div>
        </section>

        {/* ───── SERVICES MARQUEE ───── */}
        <section className="lp-services" ref={marqueeRef}>
          <div className="lp-section-head" style={{ padding: "0 24px" }}>
            <h2 className="lp-section-title">Everything You Need</h2>
            <p className="lp-section-sub">Browse our wide range of services</p>
          </div>
          <div className="lp-marquee" data-row="top">
            <div className="lp-marquee-track lp-marquee-left">
              {[...SERVICES.slice(0, 6), ...SERVICES.slice(0, 6)].map((s, i) => (
                <div className="lp-service-card" key={`l${i}`} data-service={s.title}><span className="lp-service-emoji">{s.icon}</span><div className="lp-svc-title">{s.title}</div><p>{s.desc}</p></div>
              ))}
            </div>
          </div>
          <div className="lp-marquee" data-row="bottom">
            <div className="lp-marquee-track lp-marquee-right">
              {[...SERVICES.slice(6, 12), ...SERVICES.slice(6, 12)].map((s, i) => (
                <div className="lp-service-card" key={`r${i}`} data-service={s.title}><span className="lp-service-emoji">{s.icon}</span><div className="lp-svc-title">{s.title}</div><p>{s.desc}</p></div>
              ))}
            </div>
          </div>
        </section>

        {/* ───── TESTIMONIALS ───── */}
        <section className="lp-testimonials" data-testid="landing-testimonials">
          <CursorEffect type="trail" />
          <div className="lp-section-head" style={{ padding: "0 24px", marginBottom: "48px" }}>
            <div className="lp-badge-sm"><Star size={14} /><span>Loved by thousands</span></div>
            <h2 className="lp-section-title">What Our Community Says</h2>
            <p className="lp-section-sub">Real reviews from Canadians like you</p>
          </div>
          <div className="lp-reviews-bento">
            {TESTIMONIALS.map((t, i) => (
              <ReviewCard key={i} testimonial={t} index={i} />
            ))}
          </div>
        </section>

        {/* ───── FOR HEROES ───── */}
        <section className="lp-heroes-section" data-testid="landing-for-heroes">
          <div className="lp-heroes-inner">
            <div className="lp-heroes-text">
              <div className="lp-badge-sm"><Zap size={14} /><span>For Heroes</span></div>
              <h2 className="lp-heroes-title">Earn on your terms</h2>
              <p className="lp-heroes-desc">Join Canada's fastest-growing On-Demand Services Marketplace. Set your own schedule, pick your jobs, and get paid by direct deposit a few days after each job.</p>
              <div className="lp-heroes-perks">
                {[
                  { icon: DollarSign, text: "Keep up to 90% of every job" },
                  { icon: Clock, text: "Flexible hours, your schedule" },
                  { icon: TrendingUp, text: "Earn badges, get more jobs" },
                  { icon: Smartphone, text: "Manage everything in-app" },
                ].map((p) => {
                  const Icon = p.icon;
                  return <div className="lp-heroes-perk" key={p.text}><Icon size={18} /><span>{p.text}</span></div>;
                })}
              </div>
              <button className="lp-btn lp-btn-primary" onClick={goAuth}>Become a Hero <ArrowRight size={16} /></button>
            </div>
            <div className="lp-heroes-stats">
              <div className="lp-heroes-stat-card">
                <span className="lp-heroes-stat-num" style={{ color: "#8448fc" }}>$42</span>
                <span className="lp-heroes-stat-lbl">Avg. hourly earning</span>
              </div>
              <div className="lp-heroes-stat-card">
                <span className="lp-heroes-stat-num" style={{ color: "#047857" }}>48h</span>
                <span className="lp-heroes-stat-lbl">Payment turnaround</span>
              </div>
              <div className="lp-heroes-stat-card">
                <span className="lp-heroes-stat-num" style={{ color: "#b45309" }}>4.9</span>
                <span className="lp-heroes-stat-lbl">Platform rating</span>
              </div>
            </div>
          </div>
        </section>

        {/* ───── CTA ───── */}
        <section className="lp-cta" ref={ctaRef} style={{ transition: 'transform 0.1s ease-out, opacity 0.1s ease-out', willChange: 'transform, opacity' }}>
          <div className="lp-cta-inner">
            <h2 className="lp-cta-title">Ready to Get Started?</h2>
            <p className="lp-cta-desc">Join thousands of satisfied clients and heroes on the platform that puts you first.</p>
            <button className="lp-btn lp-btn-primary" onClick={goAuth} data-testid="cta-get-started-btn"><img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" width="18" height="18" style={{ flexShrink: 0 }} /> Create Free Account <ArrowRight size={18} /></button>
          </div>
        </section>

        {/* ───── NEWSLETTER ───── */}
        <section className="lp-newsletter">
          <div className="lp-newsletter-inner">
            <div className="lp-newsletter-text"><h3>Stay in the loop</h3><p>Get tips, new features, and local hero stories delivered to your inbox.</p></div>
            <form className="lp-newsletter-form" onSubmit={handleNewsletter}>
              {newsletterStatus === "success" || newsletterStatus === "already" ? (
                <div className="lp-nl-success"><CheckCircle size={18} />{newsletterStatus === "already" ? "You're already subscribed!" : "You're subscribed!"}</div>
              ) : newsletterStatus === "error" ? (
                <div className="lp-nl-error"><X size={16} />Something went wrong. Please try again.</div>
              ) : (
                <><input type="email" placeholder="Enter your email" required disabled={newsletterLoading} data-testid="newsletter-email-input" /><button type="submit" disabled={newsletterLoading} data-testid="newsletter-subscribe-btn">{newsletterLoading ? 'Subscribing...' : 'Subscribe'}</button></>
              )}
            </form>
          </div>
        </section>

        {/* ───── TECHNOLOGY PARTNERS CAROUSEL ───── */}
        <section className="lp-partners">
          <div className="lp-partners-inner">
            <p className="lp-partners-label">Trusted Partners</p>
            <div className="lp-marquee-wrap">
              <div className="lp-partners-track">
                {[...Array(2)].map((_, setIdx) => (
                  <div className="lp-marquee-set" key={setIdx} aria-hidden={setIdx === 1}>
                    {[
                      { name: "Home Depot", text: true, color: "#F96305", bold: true },
                      { name: "Walmart", text: true, color: "#0071CE", bold: true },
                      { name: "Amazon", text: true, color: "#FF9900", bold: true },
                      { name: "HomeSense", text: true, color: "#D4213D", bold: true },
                      { name: "JYSK", text: true, color: "#003DA5", bold: true },
                      { name: "Structube", text: true, color: "#1A1A1A", bold: true },
                      { name: "Ashley", text: true, color: "#6B2A2A", bold: true },
                      { name: "Leon's", text: true, color: "#E31837", bold: true },
                      { name: "The Brick", text: true, color: "#ED1C24", bold: true },
                      { name: "Stripe", src: "https://cdn.simpleicons.org/stripe/635BFF", color: "#635BFF" },
                      { name: "Google Cloud", src: "https://cdn.simpleicons.org/googlecloud/4285F4", color: "#4285F4" },
                      { name: "AWS", text: true, color: "#FF9900", bold: true },
                      { name: "Microsoft Azure", text: true, label: "Azure", color: "#0078D4" },
                      { name: "OpenAI", text: true, color: "#412991", bold: true },
                      { name: "Anthropic", src: "https://cdn.simpleicons.org/anthropic/D4A27F", color: "#D4A27F" },
                      { name: "NVIDIA", src: "https://cdn.simpleicons.org/nvidia/76B900", color: "#76B900" },
                      { name: "React", src: "https://cdn.simpleicons.org/react/61DAFB", color: "#61DAFB" },
                      { name: "Twilio", text: true, color: "#F22F46" },
                      { name: "SendGrid", text: true, color: "#1A82E2" },
                      { name: "Resend", src: "https://cdn.simpleicons.org/resend/FFFFFF", color: "#FFFFFF" },
                      { name: "LogRocket", text: true, color: "#764ABC" },
                      { name: "QuickBooks", src: "https://cdn.simpleicons.org/quickbooks/2CA01C", color: "#2CA01C" },
                      { name: "Render", src: "https://cdn.simpleicons.org/render/46E3B7", color: "#46E3B7" },
                      { name: "Didit", text: true, color: "#8448fc" },
                      { name: "HubSpot", src: "https://cdn.simpleicons.org/hubspot/FF7A59", color: "#FF7A59" },
                      { name: "Airbnb", src: "https://cdn.simpleicons.org/airbnb/FF5A5F", color: "#FF5A5F" },
                      { name: "Vrbo", text: true, color: "#3C5B93", bold: true },
                      { name: "Booking.com", src: "https://cdn.simpleicons.org/bookingdotcom/003580", color: "#003580" },
                      { name: "Grok", text: true, color: "#1DA1F2", bold: true },
                      { name: "110binaries", text: true, color: "#8C52FF" },
                    ].map((p) => (
                      <div className="lp-partner-item" key={p.name}>
                        {p.text ? (
                          <span className="lp-partner-text" style={{ color: p.color, fontWeight: p.bold ? 800 : 700 }}>{p.label || p.name}</span>
                        ) : (
                          <img
                            src={p.src}
                            alt={p.name}
                            className="lp-partner-logo"
                            loading="lazy"
                            width="28"
                            height="28"
                          />
                        )}
                        <span className="lp-partner-name">{p.name}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ───── FOOTER ───── */}
        <footer className="lp-footer">
          <div className="lp-footer-inner">
            <div className="lp-footer-brand">
              <picture>
                <source srcSet="/logo-light.webp" type="image/webp" />
                <img src="/logo-light.png" alt="taskeri® - On-Demand Gig Platform" loading="lazy" style={{ height: 'auto', width: 120 }} onClick={() => navigate("/")} />
              </picture>
              <p>On-demand Heroes at your service. Connect, track, and get things done.</p>
              <p className="lp-footer-service-area" data-testid="footer-service-area"><MapPin size={12} /> Proudly serving all provinces and territories across Canada</p>
            </div>
            <div className="lp-footer-links"><a href="/about">About Us</a><a href="/contact">Contact Us</a></div>
            <div className="lp-footer-links"><a href="/help">Help Center</a><a href="/safety">Trust & Safety</a></div>
            <div className="lp-footer-follow">
              <h4 className="lp-footer-follow-heading">Follow us on</h4>
              <div className="lp-footer-social">
                {[{ Icon: Facebook, href: "https://www.facebook.com/taskeri.co", l: "Facebook" }, { Icon: Instagram, href: "https://www.instagram.com/taskeri.co", l: "Instagram" }, { Icon: Youtube, href: "https://www.youtube.com/@taskeri", l: "YouTube" }].map(({ Icon, href, l }) => (
                  <a key={l} href={href} target="_blank" rel="noopener noreferrer" aria-label={l}><Icon size={18} /></a>
                ))}
                <a href="https://www.tiktok.com/@taskeri.co" target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="footer-tiktok-link">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M16.6 5.82s.51.5 0 0A4.278 4.278 0 0 1 15.54 3h-3.09v12.4a2.592 2.592 0 0 1-2.59 2.5c-1.42 0-2.6-1.16-2.6-2.6 0-1.72 1.66-3.01 3.37-2.48V9.66c-3.45-.46-6.47 2.22-6.47 5.64 0 3.33 2.76 5.7 5.69 5.7 3.14 0 5.69-2.55 5.69-5.7V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3s-1.88.09-3.24-1.48z"/></svg>
                </a>
              </div>
            </div>
          </div>
          <div className="lp-footer-bottom">
            <div>
              <p>&copy; 2026 taskeri® Technologies Inc. All rights reserved.</p>
              <p style={{fontSize:'0.75em', color:'#94a3b8', marginTop:'4px'}}>taskeri<sup style={{fontSize:'0.6em'}}>®</sup> is a registered trademark of taskeri® Technologies Inc.</p>
            </div>
            <div className="lp-footer-legal"><a href="/privacy">Privacy Policy</a><span>&middot;</span><a href="/cookie-policy">Cookie Policy</a><span>&middot;</span><a href="/accessibility">Accessibility</a></div>
          </div>
        </footer>
      </div>

      {/* Cookie notice is mounted globally in App.js */}
    </>
  );
};

export default Landing;
